import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';

import { GUIDED_WORKFLOWS_OPEN_SUBCATEGORY_COOKIE_NAME } from 'QuickflowsApp/ts/constants';
import {
    ExpandablePageGuidedWorkflows,
    GuidedWorkflowStep,
    GuidedWorkflowSubcategory,
    PageGuidedWorkflows,
    SearchResultsHTML,
} from 'GuidedWorkflowsApp/ts/interfaces/guided-workflows.response';

import { CookieService } from 'ngx-cookie-service';

/**
 *
 * Guided Workflows Menu Component
 *
 * The guided workflows menu that lives on the right hand side of the screen.
 * Controls the workflow content to interact with the page its hosted in.
 *
 * Input Properties:
 *   page_path: the page path url to get categories
 *   categories: list of PageGuidedWorkflows to display.
 *
 */

@Component({
    standalone: false,
    selector: 'ycn-guided-workflows-menu',
    templateUrl: './guided-workflows-menu.template.html',
})
export class GuidedWorkflowsMenuComponent implements OnChanges {
    @Input({ required: true }) pagePath?: string;

    @Input() categories: PageGuidedWorkflows[] = [];

    @Input() selectedSubcategoryId: number | null = null;

    expandableCategories: ExpandablePageGuidedWorkflows[] = [];

    selectedSteps: GuidedWorkflowStep[] = [];

    selectedCategory: ExpandablePageGuidedWorkflows | null = null;

    selectedSubcategory!: GuidedWorkflowSubcategory;

    searchInput: string = '';

    searchResults: SearchResultsHTML[] = [];

    constructor(private sanitizer: DomSanitizer, private cookieService: CookieService) {}

    ngOnChanges(changes: SimpleChanges): void {
        this.expandableCategories = this.categories.map((category) => ({
            ...category,
            expanded: false,
        }));
        if (changes?.selectedSubcategoryId && this.selectedSubcategoryId) {
            this.setStepsBySubcategory(this.selectedSubcategoryId);
        }
    }

    get isSearching(): boolean {
        return this.searchInput.length > 0;
    }

    get searchIcon(): string {
        return this.isSearching ? 'icon-close' : 'icon-search';
    }

    clearSteps(): void {
        this.selectedSteps = [];
        this.expandableCategories = this.categories.map((category) => ({
            ...category,
            expanded: this.selectedCategory?.id === category.id,
        }));
    }

    clearSearch(): void {
        this.searchInput = '';
        this.searchResults = [];
    }

    setSelectedSubcategory(subcategory: GuidedWorkflowSubcategory): void {
        this.selectedSubcategory = subcategory;
        this.cookieService.set(GUIDED_WORKFLOWS_OPEN_SUBCATEGORY_COOKIE_NAME, this.selectedSubcategory.id.toString(), {
            path: '/',
        });
        this.trackSubcategoryUserClick();
    }

    searchResultsClickHandler(subcategoryId: number): void {
        this.setStepsBySubcategory(subcategoryId);
        this.trackSubcategoryUserClick();
    }

    searchMatchResults(): void {
        if (this.searchInput.length === 0 || this.searchInput.length < 3) {
            this.clearSearch();
            return;
        }
        const newResults: SearchResultsHTML[] = [];
        const textRegEx = new RegExp(this.searchInput, 'gi');
        const excludeTagsRegEx = new RegExp(`(?<!<[^>]*)${this.searchInput}`, 'gi');
        this.categories.forEach((category) => {
            category.subcategories.forEach((subcategory) => {
                const subcategoryHTML = subcategory.title.replace(textRegEx, `<b>${this.searchInput}</b>`);
                const subcategoryMatch = subcategoryHTML !== subcategory.title;
                subcategory.steps.forEach((step) => {
                    const textContent = step.text_content;
                    if (textContent.match(excludeTagsRegEx)) {
                        newResults.push({
                            matchHTML: this.sanitizer.bypassSecurityTrustHtml(
                                textContent.replace(excludeTagsRegEx, `<b>${this.searchInput}</b>`)
                            ) as string,
                            locationHTML: `${category.title} > ${subcategoryHTML}`,
                            subcategoryId: subcategory.id,
                        });
                    }
                    if (subcategoryMatch && !newResults.find((result) => result.subcategoryId === subcategory.id)) {
                        newResults.push({
                            matchHTML: subcategoryHTML,
                            locationHTML: category.title,
                            subcategoryId: subcategory.id,
                        });
                    }
                });
            });
        });
        this.searchResults = newResults;
    }

    private setStepsBySubcategory(subcategoryId: number): void {
        this.clearSearch();
        this.categories.forEach((category) => {
            category.subcategories.forEach((subcategory) => {
                if (subcategory.id === subcategoryId) {
                    this.selectedCategory = { ...category, expanded: false };
                    this.selectedSubcategory = subcategory;
                    this.selectedSteps = subcategory.steps;
                }
            });
        });
    }

    private trackSubcategoryUserClick(): void {
        window.pendoTrack('Guided Workflow Click', {
            title: this.selectedSubcategory.title,
        });
    }
}
